import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import moment from 'moment';
import React, { useMemo } from 'react';
import { DateRangePicker } from 'react-date-range';
import { View, Platform, StyleSheet, Pressable, Dimensions, type ViewStyle, type StyleProp } from 'react-native';
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { Text } from 'tamagui';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import DatePicker from 'react-native-date-picker';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import { subscribeResize, getWindowSize } from '@utils/resizeListenerSingleton';
import { t } from 'i18next';
import { NESTED_PICKER_Z_INDEX } from '@components/modals/shared/adaptiveModalTheme';

const webCreatePortal =
	Platform.OS === 'web' && typeof document !== 'undefined'
		? require('react-dom').createPortal
		: null;

interface DefaultDates {
  noIcons?: boolean;
  startDate: Date | string | null;
  endDate: Date | string | null;
  forceColumn?: boolean;
  /** When true, uses compact inline layout for toolbar/header placement */
  inline?: boolean;
  dateCallback?: (date: { startDate: Date, endDate: Date }) => void;
  /** Merged last onto the outer container (e.g. width / maxWidth overrides) */
  style?: StyleProp<ViewStyle>;
}

// Dark theme CSS for react-date-range (injected on web, applied when wrapper has id)
const RDR_DARK_THEME_CSS = `
#rdr-date-range-modal-dark .rdrCalendarWrapper { color: #e2e8f0; background: rgba(30, 41, 59, 0.95) !important; }
#rdr-date-range-modal-dark .rdrDateDisplayWrapper { background-color: rgba(30, 41, 59, 0.8); }
#rdr-date-range-modal-dark .rdrDateDisplayItem { background-color: rgba(51, 65, 85, 0.9); box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
#rdr-date-range-modal-dark .rdrDateDisplayItem input { color: #94a3b8; }
#rdr-date-range-modal-dark .rdrDateDisplayItemActive input { color: #cbd5e1; }
#rdr-date-range-modal-dark .rdrMonthAndYearPickers select { color: #e2e8f0; }
#rdr-date-range-modal-dark .rdrMonthAndYearPickers select:hover { background-color: rgba(255,255,255,0.08); }
#rdr-date-range-modal-dark .rdrNextPrevButton { background: rgba(51, 65, 85, 0.9); }
#rdr-date-range-modal-dark .rdrNextPrevButton:hover { background: rgba(71, 85, 105, 0.9); }
#rdr-date-range-modal-dark .rdrPprevButton i { border-color: transparent #94a3b8 transparent transparent; }
#rdr-date-range-modal-dark .rdrNextButton i { border-color: transparent transparent transparent #94a3b8; }
#rdr-date-range-modal-dark .rdrWeekDay { color: #94a3b8; }
#rdr-date-range-modal-dark .rdrDay { color: #f1f5f9; }
#rdr-date-range-modal-dark .rdrDayNumber span { color: #f1f5f9 !important; }
#rdr-date-range-modal-dark .rdrDayPassive .rdrDayNumber span { color: #64748b !important; }
#rdr-date-range-modal-dark .rdrDayDisabled { background-color: rgba(30, 41, 59, 0.5); }
#rdr-date-range-modal-dark .rdrDayDisabled .rdrDayNumber span { color: #64748b; }
#rdr-date-range-modal-dark .rdrMonthName { color: #94a3b8; }
#rdr-date-range-modal-dark .rdrDayStartPreview, #rdr-date-range-modal-dark .rdrDayInPreview, #rdr-date-range-modal-dark .rdrDayEndPreview { background: rgba(96, 165, 250, 0.15); }
#rdr-date-range-modal-dark .rdrDefinedRangesWrapper { background: rgba(30, 41, 59, 0.95); border-color: rgba(71, 85, 105, 0.8); }
#rdr-date-range-modal-dark .rdrStaticRange { background: transparent; border-color: rgba(71, 85, 105, 0.5); }
#rdr-date-range-modal-dark .rdrStaticRange:hover .rdrStaticRangeLabel, #rdr-date-range-modal-dark .rdrStaticRange:focus .rdrStaticRangeLabel { background: rgba(71, 85, 105, 0.5); }
#rdr-date-range-modal-dark .rdrStaticRangeLabel { color: #e2e8f0; }
#rdr-date-range-modal-dark .rdrInputRangeInput { background: rgba(51, 65, 85, 0.8); border-color: rgba(71, 85, 105, 0.8); color: #e2e8f0; }
#rdr-date-range-modal-dark .rdrInputRangeInput:focus, #rdr-date-range-modal-dark .rdrInputRangeInput:hover { border-color: #60a5fa; }
`;

const RangePicker = ({ forceColumn, noIcons, inline, startDate, endDate, dateCallback, style }: DefaultDates) => {
  const theme = themeSelector();
  const [isVisible, setIsVisible] = React.useState(false);
  const [startDateVisible, setStartDateVisible] = React.useState(false);
  const [endDateVisible, setEndDateVisible] = React.useState(false);
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const [screenWidth, setScreenWidth] = React.useState(
    Platform.OS === 'web' ? (typeof window !== 'undefined' ? getWindowSize().width : Dimensions.get('window').width) : Dimensions.get('window').width
  );

  const isMobile = screenWidth < 768;
  const isTablet = screenWidth >= 768 && screenWidth < 1024;

  React.useEffect(() => {
    if (Platform.OS === 'web') {
      const handleResize = () => {
        const nextWidth = Math.round(getWindowSize().width);
        setScreenWidth((prev) => (prev === nextWidth ? prev : nextWidth));
      };
      const unsub = subscribeResize(handleResize);
      return unsub;
    }
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      const nextWidth = Math.round(window.width);
      setScreenWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });
    return () => subscription?.remove();
  }, []);

  // Close modal on Escape key (web)
  React.useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isVisible]);

  // Inject dark theme CSS for react-date-range (web only, once)
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const styleId = 'rdr-date-range-dark-theme';
    if (!document.getElementById(styleId)) {
      const el = document.createElement('style');
      el.id = styleId;
      el.textContent = RDR_DARK_THEME_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Validate and normalize dates
  const validateDate = (date: Date | undefined | null): Date => {
    if (!date) return new Date();
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const validStartDate = validateDate(startDate);
  const validEndDate = validateDate(endDate);

  const [dateVals, setDateVals] = React.useState({
    startDate: validStartDate,
    endDate: validEndDate,
  });

  const [dateRange, setDateRange] = React.useState({
    startDate: validStartDate,
    endDate: validEndDate,
    key: 'selectionChart'
  });

  // Update dateVals and dateRange when props change. Use primitive (getTime()) in deps so we
  // only run when the actual date values change, not when the parent re-renders with new Date refs.
  // This avoids unnecessary effect runs and potential update loops.
  const startTime = validStartDate.getTime();
  const endTime = validEndDate.getTime();
  const syncFromPropsRef = React.useRef(false);
  React.useEffect(() => {
    const newStart = validateDate(startDate);
    const newEnd = validateDate(endDate);
    syncFromPropsRef.current = true;
    setDateVals(prev => {
      const startChanged = prev.startDate.getTime() !== newStart.getTime();
      const endChanged = prev.endDate.getTime() !== newEnd.getTime();
      if (!startChanged && !endChanged) return prev;
      return { startDate: newStart, endDate: newEnd };
    });
    setDateRange(prev => {
      const startChanged = prev.startDate.getTime() !== newStart.getTime();
      const endChanged = prev.endDate.getTime() !== newEnd.getTime();
      if (!startChanged && !endChanged) return prev;
      return { startDate: newStart, endDate: newEnd, key: 'selectionChart' };
    });
    const t = setTimeout(() => { syncFromPropsRef.current = false; }, 0);
    return () => clearTimeout(t);
  }, [startTime, endTime]);

  const handlePress = (type: string) => {
    if (Platform.OS === "web") {
      setIsVisible(true);
    } else {
      if (type === "start") {
        setStartDateVisible(true);
      } else {
        setEndDateVisible(true);
      }
    }
  };

  const handleDateConfirm = (date: Date, type: 'start' | 'end') => {
    if (type === 'start') {
      const endDate = dateVals.endDate < date ? date : dateVals.endDate;
      const newVals = { startDate: date, endDate };
      setDateVals(newVals);
      dateCallback?.(newVals);
    } else {
      const newVals = { ...dateVals, endDate: date };
      setDateVals(newVals);
      dateCallback?.({ startDate: newVals.startDate, endDate: date });
    }
  };

  // Gradient colors matching Hero.tsx
  const gradientColors = theme
    ? ['rgba(200, 162, 200, 0.15)', 'rgba(135, 206, 235, 0.15)']
    : ['rgba(200, 162, 200, 0.25)', 'rgba(135, 206, 235, 0.25)'];

  const webPickerOverlay = useMemo(() => {
    if (Platform.OS !== 'web' || !isVisible) return null;

    const pickerModal = (
      <View
        pointerEvents="box-none"
        style={[
          modernStyles.webOverlayRoot,
          { zIndex: NESTED_PICKER_Z_INDEX },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('Close')}
          onPress={() => setIsVisible(false)}
          style={modernStyles.webOverlayBackdrop}
        />
        <View
          style={[
            modernStyles.modalContainer,
            theme ? modernStyles.modalContainerLight : modernStyles.modalContainerDark,
            {
              minWidth: isMobile ? '90%' : isTablet ? 600 : 700,
              maxWidth: isMobile ? '95%' : 800,
              zIndex: NESTED_PICKER_Z_INDEX + 1,
            },
          ]}
        >
          <View style={modernStyles.modalContent}>
            <LinearGradient
              colors={gradientColors}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={modernStyles.modalGradient}
            />

            <Animatable.View animation="fadeIn" delay={200} style={modernStyles.closeButtonContainer}>
              <Pressable
                style={modernStyles.closeButton}
                onPress={() => setIsVisible(false)}
              >
                <Text style={modernStyles.closeButtonText}>{t('Close')}</Text>
              </Pressable>
            </Animatable.View>

            <View
              style={modernStyles.pickerContainer}
              nativeID={!theme ? 'rdr-date-range-modal-dark' : undefined}
            >
              <DateRangePicker
                style={{ flex: 1 }}
                ranges={[
                  {
                    startDate: dateVals.startDate,
                    endDate: dateVals.endDate,
                    key: 'selectionChart',
                  },
                ]}
                rangeColors={theme ? ['#3B82F6'] : ['#60A5FA']}
                onCanceled={() => setIsVisible(false)}
                onChange={(ranges) => {
                  if (syncFromPropsRef.current) return;
                  const { selectionChart } = ranges;
                  const validatedStart = moment(selectionChart.startDate).startOf('day').toDate();
                  const validatedEnd = moment(selectionChart.endDate).endOf('day').toDate();
                  setDateRange({
                    startDate: validatedStart,
                    endDate: validatedEnd,
                    key: 'selectionChart',
                  });
                  setDateVals({
                    startDate: validatedStart,
                    endDate: validatedEnd,
                  });
                  dateCallback?.({ startDate: validatedStart, endDate: validatedEnd });
                  setIsVisible(false);
                }}
              />
            </View>
          </View>
        </View>
      </View>
    );

    return webCreatePortal ? webCreatePortal(pickerModal, document.body) : pickerModal;
  }, [
    isVisible,
    theme,
    isMobile,
    isTablet,
    gradientColors,
    dateVals.startDate,
    dateVals.endDate,
    dateCallback,
  ]);

  return (
    <View 
      style={[
        modernStyles.container,
        {
          borderColor: theme ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.15)',
          maxWidth: 400,
        },
        inline && {
          flex: undefined,
          flexGrow: 0,
          width: '100%',
          marginTop: 0,
          marginHorizontal: 0,
        },
        style,
      ]}
    >
      {/* Floating Blur Elements - Matching Hero.tsx */}
      {Platform.OS === 'web' && (
        <>
          <View style={[modernStyles.floatingBlur, { top: -20, left: -20 }]} />
          <View style={[modernStyles.floatingBlur, { bottom: -20, right: -20 }]} />
        </>
      )}
      {webPickerOverlay}

      {/* Mobile Date Pickers */}
      {Platform.OS !== 'web' && (
        <>
          <DatePicker
            modal
            open={startDateVisible}
            mode="date"
            date={dateVals.startDate}
            onConfirm={(date) => {
              setStartDateVisible(false);
              handleDateConfirm(date, 'start');
            }}
            onCancel={() => setStartDateVisible(false)}
          />
          <DatePicker
            modal
            open={endDateVisible}
            mode="date"
            date={dateVals.endDate < dateVals.startDate ? dateVals.startDate : dateVals.endDate}
            minimumDate={dateVals.startDate}
            onConfirm={(date) => {
              setEndDateVisible(false);
              handleDateConfirm(date, 'end');
            }}
            onCancel={() => setEndDateVisible(false)}
          />
        </>
      )}
      {/* Date Buttons Container */}
      <View style={modernStyles.contentContainer}>
        {!noIcons ? (
          <Animatable.View 
            animation="fadeIn"
            delay={300}
            style={[
              modernStyles.dateButtonContainer,
              !theme && modernStyles.dateButtonContainerDark,
              isMobile && modernStyles.dateButtonContainerMobile,
            ]}
          >
            <View style={modernStyles.iconContainer}>
              <MaterialIcons 
                name="event" 
                size={isMobile ? 18 : 20} 
                color={theme ? '#3B82F6' : '#60A5FA'} 
              />
            </View>
            
            <Pressable 
              style={[
                modernStyles.dateButton,
                modernStyles.startButton,
                isMobile && modernStyles.dateButtonMobile
              ]}
              onPress={() => handlePress('start')}
            >
              <Text
                style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile,
                ]}
                numberOfLines={1}
              >
                {moment(dateVals.startDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <Pressable 
              style={[
                modernStyles.dateButton,
                modernStyles.endButton,
                isMobile && modernStyles.dateButtonMobile
              ]}
              onPress={() => handlePress('end')}
            >
              <Text
                style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile,
                ]}
                numberOfLines={1}
              >
                {moment(dateVals.endDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <View style={modernStyles.iconContainer}>
              <FontAwesome 
                name="calendar-check-o" 
                size={isMobile ? 16 : 18} 
                color={theme ? '#10B981' : '#34D399'} 
              />
            </View>
          </Animatable.View>
        ) : (
          <Animatable.View 
            animation="fadeIn"
            delay={300}
            style={[
              modernStyles.noIconsContainer,
              { flexDirection: forceColumn || isMobile ? 'column' : 'row' }
            ]}
          >
            <Pressable 
              style={[
                modernStyles.dateButton, 
                modernStyles.startButton,
                  forceColumn || isMobile ? modernStyles.dateButtonColumn : modernStyles.dateButtonRow,
              ]}
              onPress={() => handlePress('start')}
            >
                <Text style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile
                ]}>
                  {moment(dateVals.startDate).format(globalDate).toString()}
              </Text>
            </Pressable>
            
            <Pressable 
              style={[
                modernStyles.dateButton, 
                modernStyles.endButton,
                  forceColumn || isMobile ? modernStyles.dateButtonColumn : modernStyles.dateButtonRow,
              ]}
              onPress={() => handlePress('end')}
            >
                <Text style={[
                  modernStyles.dateButtonText,
                  isMobile && modernStyles.dateButtonTextMobile
                ]}>
                  {moment(dateVals.endDate).format(globalDate).toString()}
              </Text>
            </Pressable>
          </Animatable.View>
        )}
      </View>
    </View >
  )
}

// Modern Professional Styles - Matching Hero.tsx Design System
const modernStyles = StyleSheet.create({
  webOverlayRoot: {
    // @ts-expect-error RN Web supports fixed
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  webOverlayBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  container: {
    flex: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 5,
    borderRadius: 24,
    marginHorizontal: -20,
    overflow: 'hidden',
    position: 'relative',
  },

  gradientBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },

  floatingBlur: {
    position: 'absolute',
    width: 128,
    height: 128,
    borderRadius: 64,
    backgroundColor: Platform.OS === 'web' ? 'rgba(200, 162, 200, 0.1)' : 'transparent',
    ...(Platform.OS === 'web' && {
      filter: 'blur(40px)',
    }),
    zIndex: 1,
  },

  contentContainer: {
    position: 'relative',
    zIndex: 3,
    width: '100%',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
    }),
  },

  dateButtonContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 20,
    paddingHorizontal: Platform.select({ web: 16, default: 12 }),
    paddingVertical: Platform.select({ web: 8, default: 6 }),
    minWidth: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
    }),
  },

  dateButtonContainerDark: {
    backgroundColor: 'rgba(30, 41, 59, 0.9)',
  },

  dateButtonContainerMobile: {
    flexWrap: 'wrap',
    paddingHorizontal: 8,
    paddingVertical: 6,
  },

  dateButton: {
    flex: 1,
    paddingHorizontal: Platform.select({ web: 16, default: 12 }),
    paddingVertical: Platform.select({ web: 12, default: 10 }),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: Platform.select({ web: 4, default: 2 }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    minHeight: Platform.select({ web: 44, default: 40 }),
  },

  dateButtonMobile: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginHorizontal: 2,
    minHeight: 36,
  },

  dateButtonRow: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },

  dateButtonColumn: {
    borderRadius: 16,
    marginBottom: 8,
    width: '100%',
  },

  startButton: {
    backgroundColor: '#3B82F6',
    borderStartEndRadius: 0, borderBottomEndRadius: 0,
  },

  endButton: {
    backgroundColor: '#10B981',
    borderStartStartRadius: 0, borderBottomStartRadius: 0,
  },

  dateButtonText: {
    fontSize: Platform.select({ web: 13, default: 12 }),
    fontWeight: '600',
    color: 'white',
    textAlign: 'center',
  },

  dateButtonTextMobile: {
    fontSize: 11,
  },

  iconContainer: {
    paddingHorizontal: Platform.select({ web: 8, default: 6 }),
    alignItems: 'center',
    justifyContent: 'center',
  },

  modalContainer: {
    borderRadius: 24,
    padding: Platform.select({ web: 24, default: 16 }),
    minHeight: Platform.select({ web: 500, default: 400 }),
    shadowOffset: { width: 0, height: 12 },
    shadowRadius: 24,
    elevation: 16,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
    }),
  },
  modalContainerLight: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    borderColor: 'rgba(59, 130, 246, 0.15)',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
    }),
  },
  modalContainerDark: {
    backgroundColor: 'rgba(30, 41, 59, 0.98)',
    shadowColor: '#fff',
    shadowOpacity: 0.08,
    borderColor: 'rgba(96, 165, 250, 0.2)',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
    }),
  },

  modalContent: {
    position: 'relative',
    zIndex: 1,
    flex: 1,
  },

  modalGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
  },

  closeButtonContainer: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
  },

  closeButton: {
    backgroundColor: '#EF4444',
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
  },

  closeButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },

  pickerContainer: {
    flex: 1,
    marginTop: 50,
    zIndex: 2,
  },

  noIconsContainer: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    padding: Platform.select({ web: 4, default: 2 }),
  },
});

export default RangePicker; 